import { FC } from 'react';

import { ID, ORDER_CART_TEST_ID } from './constants';
import { useHeaderExpandedState } from './hooks/useHeaderExpandedState';
import { OrderCartProps } from './model';
import { CartBox, CartFooter, CartSection, CtaGroup, EntryList, ExpandableBody } from './OrderCart.styles';
import { CartAction } from './private/CartAction';
import { CartHeader } from './private/CartHeader/CartHeader';
import { CartLegalText } from './private/CartLegalText/CartLegalText';
import { CartOutOfStock } from './private/CartOutOfStock/CartOutOfStock';
import { CartSecondaryAction } from './private/CartSecondaryAction';
import { CartTotals } from './private/CartTotals/CartTotals';
import { CartTotalsNotification } from './private/CartTotalsNotification/CartTotalsNotification';
import { CartValuePrompts } from './private/CartValuePrompts/CartValuePrompts';
import { CartVatNote } from './private/CartVatNote/CartVatNote';
import { PreorderBanner } from './private/PreorderBanner/PreorderBanner';

export const OrderCart: FC<OrderCartProps> = ({
    children,
    header,
    preorderBanner,
    cartTotals,
    cartAction,
    cartSecondaryAction,
    cartOutOfStock,
    cartValuePrompts,
    cartVatNote,
    cartLegalText,
    cartTotalsNotification,
    hidden,
    className,
    ['data-test']: dataTest = ORDER_CART_TEST_ID.ORDER_CART,
}) => {
    const { isHeaderExpanded, toggleHeaderExpand } = useHeaderExpandedState(
        header.expandable !== 'NEVER',
        header.isDefaultExpanded,
        header.isForceExpanded,
    );

    return (
        <CartSection
            id={ID.MAIN}
            aria-labelledby={ID.HEADING}
            data-test={dataTest}
            className={className}
            hidden={hidden}
        >
            <CartBox>
                <CartHeader
                    isExpanded={isHeaderExpanded}
                    onExpandClick={toggleHeaderExpand}
                    expandable={header.expandable}
                    title={header.title}
                    titleTag={header.titleTag}
                />
                <EntryList id={ID.ENTRIES_LIST} $expansionMode={header.expandable} $isExpanded={isHeaderExpanded}>
                    <ExpandableBody>{children}</ExpandableBody>
                </EntryList>
                <CartFooter>
                    {preorderBanner?.isVisible && <PreorderBanner shipmentDate={preorderBanner.shipmentDate} />}
                    {cartTotalsNotification?.isVisible && (
                        <CartTotalsNotification content={cartTotalsNotification.content} />
                    )}
                    <CartTotals data={cartTotals} />
                    {cartOutOfStock?.isVisible && <CartOutOfStock />}
                    <CtaGroup>
                        {cartAction?.isVisible && <CartAction data={cartAction} />}
                        {cartSecondaryAction?.isVisible && <CartSecondaryAction data={cartSecondaryAction} />}
                    </CtaGroup>
                    {cartValuePrompts?.isVisible && <CartValuePrompts />}
                </CartFooter>
            </CartBox>
            {cartVatNote?.isVisible && <CartVatNote />}
            {cartLegalText?.isVisible && <CartLegalText legalText={cartLegalText.legalText} />}
        </CartSection>
    );
};
